/**
 * Vue Router Configuration
 * @description Centralized routing for Autolabs
 * Phase 3: Okta Native Authentication with navigation guards
 */

import { createRouter, createWebHistory } from 'vue-router'
import { LoginCallback } from '@okta/okta-vue'
import { isLocalOnlyAuthEnabled } from '@/config/okta'

// Eagerly load share portal components to avoid slow lazy loading in dev
import SharePortalLogin from '../views/share/SharePortalLogin.vue'
import SharePortalVMs from '../views/share/SharePortalVMs.vue'
import SharePortalConsole from '../views/share/SharePortalConsole.vue'
import ShareInvalidPage from '../views/share/ShareInvalidPage.vue'
import SharePortalLayout from '../components/share/SharePortalLayout.vue'

const LocalOnlyLoginPage = () => import('../views/auth/LocalOnlyLoginPage.vue')

/**
 * @type {import('vue-router').RouteRecordRaw[]}
 */
const routes = [
  // Root redirect
  {
    path: '/',
    redirect: '/dashboard'
  },

  // Public Routes (no auth required)
  {
    path: '/login/callback',
    name: 'auth-callback',
    component: isLocalOnlyAuthEnabled ? LocalOnlyLoginPage : LoginCallback,
    meta: {
      public: true,
      title: 'Authenticating...'
    }
  },
  {
    path: '/local-login',
    name: 'local-login',
    component: LocalOnlyLoginPage,
    meta: {
      public: true,
      title: 'Local-Only Login'
    }
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('../views/auth/LogoutPage.vue'),
    meta: {
      public: true,
      title: 'Signing Out...'
    }
  },

  // Share Portal Routes (separate authentication)
  // Using eager loading to avoid slow lazy loading delays

  // Invalid share URL (no share ID provided)
  {
    path: '/share',
    name: 'share-invalid',
    component: ShareInvalidPage,
    meta: {
      public: true,
      title: 'Invalid Share Link'
    }
  },
  {
    path: '/share/:shareId',
    component: SharePortalLayout,
    children: [
      {
        path: '',
        name: 'share-login',
        component: SharePortalLogin,
        meta: {
          public: true,
          title: 'Share Portal Login'
        }
      },
      {
        path: 'vms',
        name: 'share-vms',
        component: SharePortalVMs,
        meta: {
          requiresShareSession: true,
          title: 'Share Portal - VMs'
        }
      },
      {
        path: 'vms/:vmId/console',
        name: 'share-console',
        component: SharePortalConsole,
        meta: {
          requiresShareSession: true,
          title: 'Share Portal - Console'
        }
      }
    ]
  },

  // Protected Routes with AppLayout (requires authentication)
  {
    path: '/',
    component: () => import('../components/layout/AppLayout.vue'),
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: 'assistant',
        name: 'assistant',
        component: () => import('../views/AssistantPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'assistant',
          title: 'AutoLabs AI'
        }
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('../views/DashboardPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Dashboard'
        }
      },
      {
        path: 'labs',
        name: 'labs-list',
        component: () => import('../views/labs/LabsListPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'My Labs'
        }
      },
      {
        path: 'labs/new',
        name: 'labs-create',
        component: () => import('../views/labs/CreateLabPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Create Lab'
        }
      },
      {
        path: 'labs/:id',
        name: 'lab-detail',
        component: () => import('../views/labs/LabDetailPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Lab Details'
        }
      },
      {
        path: 'labs/:id/console/:vmId',
        name: 'lab-console',
        component: () => import('../views/labs/LabConsolePage.vue'),
        meta: {
          requiresAuth: true,
          title: 'VM Console'
        }
      },
      {
        path: 'templates',
        name: 'templates',
        component: () => import('../views/TemplatesPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Lab Templates'
        }
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('../views/SettingsPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Settings'
        }
      },
      {
        path: 'docs',
        name: 'docs',
        component: () => import('../views/DocsPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Documentation'
        }
      },
      // Zscaler tenant routes
      {
        path: 'zscaler-tenants',
        name: 'zscaler-tenants',
        component: () => import('../views/zscaler/ZscalerTenantsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'zscaler.enabled',
          title: 'Zscaler Tenants'
        }
      },
      {
        path: 'zscaler-tenants/:id',
        name: 'zscaler-tenant-detail',
        component: () => import('../views/zscaler/ZscalerTenantDetailPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'zscaler.enabled',
          title: 'Zscaler Tenant Detail'
        }
      },
      {
        path: 'validation',
        redirect: { name: 'validation-runs' }
      },
      {
        path: 'validation/rules',
        name: 'validation-rules',
        component: () => import('../views/validation/ValidationRulesPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'validation.enabled',
          title: 'Validation Rules'
        }
      },
      {
        path: 'validation/runs/new',
        name: 'validation-new-run',
        component: () => import('../views/validation/ValidationNewRunPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'validation.enabled',
          title: 'New Validation Run'
        }
      },
      {
        path: 'validation/runs/:runId',
        name: 'validation-run-detail',
        component: () => import('../views/validation/ValidationRunDetailPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'validation.enabled',
          title: 'Validation Run'
        }
      },
      {
        path: 'validation/runs',
        name: 'validation-runs',
        component: () => import('../views/validation/ValidationRunsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'validation.enabled',
          title: 'Validation Runs'
        }
      },
      {
        path: 'validation/plans',
        name: 'validation-plans',
        component: () => import('../views/validation/ValidationPlansPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'validation.enabled',
          title: 'Validation Plans'
        }
      },
      {
        path: 'validation/rules/new',
        name: 'validation-rule-builder',
        component: () => import('../views/validation/ValidationRuleBuilderPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'validation.enabled',
          title: 'Rule Builder'
        }
      },
      {
        path: 'validation/rules/:ruleId/edit',
        name: 'validation-rule-edit',
        component: () => import('../views/validation/ValidationRuleBuilderPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'validation.enabled',
          title: 'Edit Rule'
        }
      },
      {
        path: 'validation/targets',
        name: 'validation-targets',
        component: () => import('../views/validation/ValidationTargetsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresFeatureFlag: 'validation.enabled',
          title: 'Validation Targets'
        }
      },
      // Admin Routes (requires admin role)
      {
        path: 'admin/motd',
        redirect: { name: 'admin-announcements' }
      },
      {
        path: 'admin/announcements',
        name: 'admin-announcements',
        component: () => import('../views/admin/AnnouncementsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Announcements'
        }
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('../views/admin/UsersPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'User Management'
        }
      },
      {
        path: 'admin/templates',
        name: 'admin-templates',
        component: () => import('../views/admin/TemplatesAdminPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Lab Templates (Admin)'
        }
      },
      {
        path: 'admin/labs',
        name: 'admin-labs',
        component: () => import('../views/admin/AdminLabsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          requiresFeatureFlag: 'ui.admin_labs_page',
          title: 'User Labs'
        }
      },
      {
        path: 'admin/feature-flags',
        name: 'admin-feature-flags',
        component: () => import('../views/admin/FeatureFlagsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Feature Flags'
        }
      },
      {
        path: 'admin/audit',
        name: 'admin-audit',
        component: () => import('../views/admin/AuditLogsPage.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true,
          title: 'Audit Logs'
        }
      }
    ]
  },

  // 404 Not Found
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundPage.vue'),
    meta: {
      public: true,
      title: 'Page Not Found'
    }
  }
]

/**
 * Create and configure Vue Router instance
 * @returns {import('vue-router').Router}
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    } else {
      return { top: 0 }
    }
  }
})

/**
 * Check if share session is valid
 * TODO: Replace with proper share session store check in Phase 4
 * @param {string} shareId - The share ID from route params
 * @returns {boolean}
 */
const hasValidShareSession = shareId => {
  const shareSessionToken = sessionStorage.getItem('share_session_token')
  const shareSessionId = sessionStorage.getItem('share_session_id')

  // Check if token exists and matches current share ID
  return !!shareSessionToken && shareSessionId === shareId
}

/**
 * Global Navigation Guard - Authentication & Authorization
 *
 * IMPORTANT: This is a SINGLE combined guard that handles all authentication.
 * Share portal routes use separate session-based auth and NEVER touch Okta.
 * Main app routes use Okta authentication via the auth store.
 *
 * Note: We cannot use Okta's navigationGuard directly because we need custom logic
 * for share portal routes and admin role checking. Instead, we check the auth store's
 * isAuthenticated state which is synced with Okta's authStateManager.
 */
router.beforeEach(async (to, from, next) => {
  const guardStart = performance.now()
  console.debug('[Guard] Started at', guardStart.toFixed(0), 'ms, to:', to.path)

  // Set page title
  document.title = to.meta.title ? `${to.meta.title} | Autolabs` : 'Autolabs'

  // =========================================
  // SHARE PORTAL ROUTES - Separate auth system
  // =========================================
  // Share portal routes NEVER use Okta - they have their own session-based auth
  if (to.path.startsWith('/share/')) {
    console.debug('[Guard] Share portal route detected')

    // Check if this share route requires session authentication
    if (to.meta.requiresShareSession) {
      const shareId = to.params.shareId

      if (!hasValidShareSession(shareId)) {
        console.warn('[Guard] Share session required. Redirecting to share login...')
        return next({
          name: 'share-login',
          params: { shareId }
        })
      }

      console.debug(
        '[Guard] Share session valid, allowing navigation at',
        (performance.now() - guardStart).toFixed(0),
        'ms'
      )
    } else {
      // Public share route (login page)
      console.debug(
        '[Guard] Public share route, allowing navigation at',
        (performance.now() - guardStart).toFixed(0),
        'ms'
      )
    }

    return next()
  }

  // =========================================
  // PUBLIC ROUTES - No auth required
  // =========================================
  if (to.meta.public) {
    console.debug(
      '[Guard] Public route, allowing navigation at',
      (performance.now() - guardStart).toFixed(0),
      'ms'
    )
    return next()
  }

  // =========================================
  // MAIN APP ROUTES - Platform authentication
  // =========================================
  // Only routes that reach here need the main app auth system
  if (to.meta.requiresAuth) {
    console.debug(
      '[Guard] Main app auth required, checking auth store at',
      (performance.now() - guardStart).toFixed(0),
      'ms'
    )

    // Import auth store dynamically to avoid circular dependency
    const { useAuthStore } = await import('@/stores/auth')
    const authStore = useAuthStore()

    // Check if user is authenticated
    if (!authStore.isAuthenticated) {
      console.warn('[Guard] Not authenticated. Initiating login...')
      // Save the original URI for post-login redirect
      await authStore.login(to.fullPath)
      // Login will navigate away from the current route, so we don't call next()
      return
    }

    console.debug('[Guard] User authenticated')

    // Check admin privileges if required
    if (to.meta.requiresAdmin && !authStore.isAdmin) {
      console.warn('[Guard] Admin access required. Access denied.')
      return next({
        name: 'dashboard',
        query: { error: 'admin_required' }
      })
    }

    // Check feature flag if required
    if (to.meta.requiresFeatureFlag) {
      const { useFeatureFlagsStore } = await import('@/stores/featureFlags')
      const ffStore = useFeatureFlagsStore()
      if (!ffStore.hasFetched) {
        await ffStore.fetchFlags()
      }
      if (!ffStore.isEnabled(to.meta.requiresFeatureFlag)) {
        console.warn(
          '[Guard] Feature flag "%s" disabled. Redirecting.',
          to.meta.requiresFeatureFlag
        )
        return next({ name: 'dashboard' })
      }
    }

    console.debug(
      '[Guard] Auth checks passed, allowing navigation at',
      (performance.now() - guardStart).toFixed(0),
      'ms'
    )
    return next()
  }

  // Default: allow navigation (shouldn't reach here normally)
  console.debug('[Guard] Default allow at', (performance.now() - guardStart).toFixed(0), 'ms')
  return next()
})

/**
 * Global After Hook - Analytics & Logging
 * Runs after navigation is confirmed
 */
router.afterEach((to, from) => {
  console.debug(
    '[Router afterEach] Navigation complete at',
    performance.now().toFixed(0),
    'ms, to:',
    to.path
  )

  // Log navigation in development
  if (import.meta.env.DEV) {
    console.debug(`[Router] Navigated from ${from.path} to ${to.path}`)
  }
})

/**
 * Navigation Error Handler
 */
router.onError(error => {
  console.error('[Router] Navigation error:', error)

  // TODO: Phase 3 - Show error notification to user
  // TODO: Phase 3 - Send error to monitoring service
})

export default router
